import { useEffect, useState } from "react";
import OrganisationLayout from "../layout/OrganisationLayout";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { schoolDetailAction, LogotAction } from "../../Redux/Action/authAction";
import { resetAuthSession, storeUser } from "../../Redux/Slice/authSlice";
import { msalInstance } from "../../Util/msalConfig";
import { clearPlatformVisitMarkers } from "../../Util/visitTracking";
import StudentsImagesModal from "../../modals/StudentsImagesModal";
import {
  organisationMemberDetailsAction,
  organisationUpdateMemberAction,
  resetOrganisationAction,
} from "../../Redux/Action/organisationAction";
import {
  formatOrganisationRole,
  getOrganisationGroupRole,
  getOrganisationRoleAccess,
  getActiveGroup,
} from "../utils/roleAccess";

const TITLE_OPTIONS = ["Mr.", "Mrs.", "Miss", "Ms.", "Dr."];

const getMemberId = (member) =>
  member?._id || member?.memberId || member?.id || member?.userId || "";

const getProfileImage = (member) =>
  member?.profileImage ||
  member?.image ||
  member?.avatar ||
  member?.profile ||
  "";

const getMemberPayload = (payload) =>
  payload?.member ||
  payload?.memberDetails ||
  payload?.details ||
  payload?.data ||
  payload ||
  {};

const profileValidationSchema = Yup.object().shape({
  title: Yup.string().required("This field is required"),
  customTitle: Yup.string().when("title", {
    is: "custom",
    then: (schema) =>
      schema
        .trim()
        .min(2, "Title must be at least 2 characters")
        .max(30, "Title cannot be longer than 30 characters")
        .required("This field is required"),
    otherwise: (schema) => schema,
  }),
  firstName: Yup.string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .max(20, "Name cannot be longer than 20 characters")
    .required("Name is required"),
  surName: Yup.string()
    .trim()
    .min(2, "Last name must be at least 2 characters")
    .max(20, "Last name cannot be longer than 20 characters"),
  email: Yup.string()
    .trim()
    .email("Please enter a valid email")
    .required("This field is required")
    .test(
      "first-letter-lowercase",
      "The first letter of the email must be lowercase",
      (value) => {
        if (!value) return true;
        return value[0] === value[0].toLowerCase();
      },
    ),
  phoneNumber: Yup.string().trim().max(20, "Phone number is too long"),
  address: Yup.string()
    .trim()
    .max(200, "Address cannot be longer than 200 characters"),
  postcode: Yup.string()
    .trim()
    .max(20, "Postcode cannot be longer than 20 characters"),
});

function OrganisationProfileEditModal({
  show,
  userData,
  isSubmitting,
  onHide,
  onSubmit,
}) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [studentModalShow, setStudentModalShow] = useState(false);

  const processedInitialValues = userData
    ? {
      title: TITLE_OPTIONS.includes(userData?.title)
        ? userData?.title
        : userData?.title
          ? "custom"
          : "",
      customTitle:
        userData?.title && !TITLE_OPTIONS.includes(userData?.title)
          ? userData?.title
          : "",
      firstName: userData?.firstName || "",
      role: userData?.role || "",
      surName: userData?.surName || "",
      email: userData?.email || "",
      phoneNumber: userData?.phoneNumber || "",
      profileImage: getProfileImage(userData),
      address: userData?.address || "",
      postcode: userData?.postcode || "",
    }
    : {
      title: "",
      customTitle: "",
      firstName: "",
      surName: "",
      email: "",
      phoneNumber: "",
      profileImage: "",
      address: "",
      postcode: "",
    };

  useEffect(() => {
    if (show) {
      setSelectedImage(null);
    }
  }, [show]);

  const formik = useFormik({
    initialValues: processedInitialValues,
    enableReinitialize: true,
    validationSchema: profileValidationSchema,
    onSubmit: (values) => {
      const finalTitle =
        values.title === "custom" ? values.customTitle.trim() : values.title;

      onSubmit({
        title: finalTitle,
        firstName: values.firstName.trim(),
        surName: values.surName.trim(),
        email: values.email.trim(),
        phoneNumber: values.phoneNumber.trim(),
        profileImage: selectedImage || values.profileImage || "",
        address: values.address.trim(),
        postcode: values.postcode.trim(),
      });
    },
  });

  const handleCloseStudentModal = () => setStudentModalShow(false);
  const handleOpenStudentModal = () => setStudentModalShow(true);

  return (
    <>
      <Modal
        size="lg"
        show={show}
        onHide={onHide}
        className="comn-modal-set create"
        centered
      >
        <Modal.Header className="d-block" style={{ border: 0 }}>
          <Row>
            <Col lg={12} className="d-flex justify-content-center mb-4">
              <div>
                <Modal.Title className="p-0">Edit Profile</Modal.Title>
                <p className="text-center mb-0">
                  Update your organisation group account details.
                </p>
              </div>
            </Col>
          </Row>
          <div className="modal-right">
            <button className="menu-dots" onClick={onHide}>
              <img
                src={require("../../Assets/Images/dotsweb.svg").default}
                alt="close"
              />
            </button>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={formik.handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Profile</Form.Label>
              <div className="upld-vd-box mt-4 mb-4">
                <div className="upld-img-side">
                  {selectedImage || formik.values.profileImage ? (
                    <div>
                      <img
                        src={selectedImage || formik.values.profileImage}
                        className="img-fluid"
                        alt="profile"
                      />
                    </div>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="80"
                        height="80"
                        viewBox="0 0 80 80"
                        fill="none"
                      >
                        <g clipPath="url(#profile-upload-icon)">
                          <path
                            d="M33.3337 53.3333H46.667C48.5003 53.3333 50.0003 51.8333 50.0003 50V33.3333H55.3003C58.267 33.3333 59.767 29.7333 57.667 27.6333L42.367 12.3333C41.067 11.0333 38.967 11.0333 37.667 12.3333L22.367 27.6333C20.267 29.7333 21.7337 33.3333 24.7003 33.3333H30.0003V50C30.0003 51.8333 31.5003 53.3333 33.3337 53.3333ZM20.0003 60H60.0003C61.8337 60 63.3337 61.5 63.3337 63.3333C63.3337 65.1667 61.8337 66.6667 60.0003 66.6667H20.0003C18.167 66.6667 16.667 65.1667 16.667 63.3333C16.667 61.5 18.167 60 20.0003 60Z"
                            fill="#4318FF"
                          />
                        </g>
                        <defs>
                          <clipPath id="profile-upload-icon">
                            <rect width="80" height="80" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      <div className="upld-text">
                        <h3>Update Photo</h3>
                      </div>
                    </>
                  )}
                </div>
                <div className="upld-btn-side">
                  <h2>Select</h2>
                  <p>550 x 1200 recommended</p>
                  <div className="mt-5" onClick={handleOpenStudentModal}>
                    <Link to="#">Update Photo</Link>
                  </div>
                </div>
              </div>
            </Form.Group>

            <Row>


              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Select
                    className="input-set"
                    name="title"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.title}
                  >
                    <option value="">Select Title</option>
                    {TITLE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                    <option value="custom">Other</option>
                  </Form.Select>
                  {formik.submitCount > 0 && formik.errors.title && (
                    <div className="error text-danger">
                      {formik.errors.title}
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                {formik.values.title === "custom" && (
                  <Form.Group className="mb-3">
                    <Form.Label>Other Title</Form.Label>
                    <Form.Control
                      type="text"
                      className="input-set"
                      placeholder="Enter other title"
                      name="customTitle"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.customTitle}
                    />
                    {formik.submitCount > 0 && formik.errors.customTitle && (
                      <div className="error text-danger">
                        {formik.errors.customTitle}
                      </div>
                    )}
                  </Form.Group>
                )}
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    className="input-set"
                    placeholder="Type Here"
                    name="firstName"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.firstName}
                  />
                  {formik.submitCount > 0 && formik.errors.firstName && (
                    <div className="error text-danger">
                      {formik.errors.firstName}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Surname</Form.Label>
                  <Form.Control
                    type="text"
                    className="input-set"
                    placeholder="Type Here"
                    name="surName"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.surName}
                  />
                  {formik.submitCount > 0 && formik.errors.surName && (
                    <div className="error text-danger">
                      {formik.errors.surName}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    className="input-set"
                    placeholder="Type Here"
                    name="email"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                  />
                  {formik.submitCount > 0 && formik.errors.email && (
                    <div className="error text-danger">
                      {formik.errors.email}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="text"
                    className="input-set"
                    placeholder="Type Here"
                    name="phoneNumber"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.phoneNumber}
                  />
                  {formik.submitCount > 0 && formik.errors.phoneNumber && (
                    <div className="error text-danger">
                      {formik.errors.phoneNumber}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                className="input-set organisation-profile-textarea"
                placeholder="Type Here"
                name="address"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.address}
              />
              {formik.submitCount > 0 && formik.errors.address && (
                <div className="error text-danger">{formik.errors.address}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Postcode</Form.Label>
              <Form.Control
                type="text"
                className="input-set"
                placeholder="Type Here"
                name="postcode"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.postcode}
              />
              {formik.submitCount > 0 && formik.errors.postcode && (
                <div className="error text-danger">
                  {formik.errors.postcode}
                </div>
              )}
            </Form.Group>

            <Modal.Footer className="border-none justify-content-center">
              <Button
                variant="primary"
                className="modal-f-btn"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Updating..." : "Update"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      <StudentsImagesModal
        studentModalshow={studentModalShow}
        handleCloseStudentModal={handleCloseStudentModal}
        setSlectedImage={setSelectedImage}
      />
    </>
  );
}

export default function OrganisationGroupsOrgGroupDetails() {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.authSlice.userData?.payload);
  const organisationGroup = getActiveGroup(userData);
  const access = getOrganisationRoleAccess(userData);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const memberId = getMemberId(userData);
  const profileImage = getProfileImage(userData);
  const phoneNumber = userData?.phoneNumber || organisationGroup?.phoneNumber;
  const address = userData?.address || organisationGroup?.address;
  const postcode = userData?.postcode || organisationGroup?.postcode;

  const logoutFunc = async () => {
    try {
      await dispatch(LogotAction());
    } catch (error) { }

    try {
      sessionStorage.clear();
      msalInstance.clearCache();
    } catch (error) { }

    clearPlatformVisitMarkers();
    localStorage.removeItem("token");
    dispatch(resetAuthSession());
    dispatch(resetOrganisationAction());
    window.location.href = "/";
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  const handleOpenEditModal = () => {
    setShowEditModal(true);
  };

  const handleProfileUpdate = async (formData) => {
    if (!memberId) {
      toast.error("Unable to identify your organisation group member profile.");
      return;
    }

    setIsSubmitting(true);

    const response = await dispatch(
      organisationUpdateMemberAction({
        memberId,
        ...formData,
      }),
    );

    if (response?.error) {
      toast.error(response?.payload?.message || "Unable to update profile.");
      setIsSubmitting(false);
      return;
    }

    const refreshResponse = await dispatch(
      organisationMemberDetailsAction(memberId),
    );
    const refreshedPayload =
      refreshResponse?.payload?.data || refreshResponse?.payload || {};
    const refreshedMember = getMemberPayload(refreshedPayload);

    if (
      !refreshResponse?.error &&
      Object.keys(refreshedMember || {}).length > 0
    ) {
      dispatch(
        storeUser({
          ...userData,
          ...refreshedMember,
          image: getProfileImage(refreshedMember) || userData?.image || "",
          profileImage:
            getProfileImage(refreshedMember) || userData?.profileImage || "",
        }),
      );
    } else {
      const infoResponse = await dispatch(schoolDetailAction());

      if (!infoResponse?.error && infoResponse?.payload?.data) {
        dispatch(
          storeUser({
            ...userData,
            ...infoResponse.payload.data,
          }),
        );
      } else {
        dispatch(
          storeUser({
            ...userData,
            ...formData,
            image: formData?.profileImage || userData?.image || "",
            profileImage:
              formData?.profileImage || userData?.profileImage || "",
          }),
        );
      }
    }

    toast.success(
      response?.payload?.message || "Profile updated successfully.",
    );
    setIsSubmitting(false);
    handleCloseEditModal();
  };

  return (
    <OrganisationLayout>
      <Container fluid>
        <div className="heading-top mt-5 mb-2">
          <p className="organization-crumb">Your Account</p>
          <h2>Organisation Settings</h2>
        </div>

        <Row className="g-4 mt-1 justify-content-center">
          <Col xxl={6} xl={7} lg={9}>
            <div className="cmn-card-color organisation-profile-card">
              <div className="student-view">
                <img
                  src={profileImage || require("../../Assets/Images/girl.png")}
                  alt="Organisation profile"
                />
                <h2>
                  {[userData?.firstName, userData?.surName]
                    .filter(Boolean)
                    .join(" ") || "Organisation Group User"}
                </h2>
                <p>{organisationGroup?.name || "Organisation Group Module"}</p>
                <p>
                  {formatOrganisationRole(getOrganisationGroupRole(userData))}
                </p>
                <div className="back-page-title">
                  <button onClick={handleOpenEditModal} className="edit-btn py-0">
                    <img
                      className="mt-3 mb-0"
                      src={require("../../Assets/Images/edit.svg").default}
                    />
                  </button>
                </div>
              </div>

              <div className="organisation-profile-grid">
                <div>
                  <span>Email</span>
                  <strong>{userData?.email || "Not available"}</strong>
                </div>
                <div>
                  <span>Phone</span>
                  <strong>{phoneNumber || "Not set"}</strong>
                </div>
                <div>
                  <span>Address</span>
                  <strong>{address || "Not available"}</strong>
                </div>
                <div>
                  <span>Postcode</span>
                  <strong>{postcode || "Not set"}</strong>
                </div>
              </div>

              <div className="organisation-profile-actions">
                <div className="organisation-note-banner organisation-note-banner-inline mt-4">
                  Your account currently has{" "}
                  <strong>{access.roleLabel.toLowerCase()}</strong> access
                  within this organisation group.
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <OrganisationProfileEditModal
        show={showEditModal}
        userData={userData}
        isSubmitting={isSubmitting}
        onHide={handleCloseEditModal}
        onSubmit={handleProfileUpdate}
      />
    </OrganisationLayout>
  );
}
